import { StyledP } from 'components';
import { optionGet, optionFind } from 'faunctions';
import { showIf } from 'helpers/conditionals';
import { isEmpty } from 'helpers/arrays';
import QuoteDataCartItem from 'mage-swagfaces/quote/QuoteDataCartItem';
import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import type { Dispatch } from 'redux';
import {
  removeCartItemRequest as _removeCartItemRequest,
  updateCartItemQuantityRequest,
  fetchCartRequest as _fetchCartRequest
} from 'store/cart/actions';
import { cartItemsSelector, cartLoadingStateSelector, cartItemsLoadingStateSelector } from 'store/cart/selectors';
import { GlobalState, isSuccess, isNotFound, LoadingState, isFetching } from 'store/constants';
import { customerCustomAttributesSelector, customerEmailSelector, customerIdSelector } from 'store/customer/selectors';
import { openGiftOptionsModal } from 'store/modal/actions';
import CartItem from './CartItem';
import { CartItemListSkeleton, EmptyCartContent, StyledCartItemList, ULink } from './styled';
import { AUQuoteDataCartItem } from 'types/magento';
import { useProductSelectors } from 'hooks/useProductSelectors';
import { Option } from 'types/Option';
import { useSplitTreatment } from 'hooks/splits/useSplitTreatment';
import {
  SPLIT_IO_FEATURE_FLAG_CART_PRICE_TRANSPARENCY,
  SPLIT_IO_FEATURE_FLAG_CART_PRICE_TRANSPARENCY_COOKIE
} from 'constants/split';

interface CartItemListProps {
  cartItems: AUQuoteDataCartItem[];
  updateCartItemQuantity: ({ value, label }: Option) => (cartItem: AUQuoteDataCartItem) => void;
  removeCartItemRequest: (item: AUQuoteDataCartItem) => void;
  fetchCartRequest: () => void;
  customAttributes: any;
  cartLoadingState: any;
  openGiftOptions: (data?: string) => void;
  removeable?: boolean;
  cartItemLoadingState: LoadingState;
  email: string;
  customerId: string;
}

const CartItemList = ({
  cartItems,
  updateCartItemQuantity,
  removeCartItemRequest,
  fetchCartRequest,
  customAttributes,
  openGiftOptions,
  cartLoadingState,
  cartItemLoadingState,
  removeable = true,
  email,
  customerId
}: CartItemListProps) => {
  //Gets a fresh cart when page count changes
  //Future optimization would be to get a fresh cart whenever any project options change
  const prevPageCountsRef = useRef<string>('');
  const productSelectors = useProductSelectors();

  const { treatmentStatus: productOptionPriceTransparencySplit } = useSplitTreatment(
    SPLIT_IO_FEATURE_FLAG_CART_PRICE_TRANSPARENCY,
    SPLIT_IO_FEATURE_FLAG_CART_PRICE_TRANSPARENCY_COOKIE
  );

  useEffect(() => {
    const currentPageCounts = JSON.stringify(
      cartItems.map((item) => ({ id: item.item_id, pageCount: item.page_count }))
    );

    if (prevPageCountsRef.current !== currentPageCounts) {
      fetchCartRequest();
      prevPageCountsRef.current = currentPageCounts;
    }
  }, [cartItems, fetchCartRequest]);

  const flashId = optionFind((x: { attribute_code: string }) => x.attribute_code === 'flash_id', customAttributes)
    .chain(optionGet('value'))
    .getOrElse('');

  const flashToken = optionFind(
    (x: { attribute_code: string }) => x.attribute_code === 'flash_auth_token',
    customAttributes
  )
    .chain(optionGet('value'))
    .getOrElse('');

  const getButtonList = (item: AUQuoteDataCartItem, productOptionPriceTransparencySplit: boolean) => {
    const itemId = optionGet('item_id')(item).getOrElse(null);

    const isGiftWrapAvailable = item.gift_wrapping_available;
    const isGiftMessagAvailable = item.gift_message_available;

    const style = productOptionPriceTransparencySplit
      ? { fontSize: '12px', lineHeight: '150%', letterSpacing: '1px', weight: 600 }
      : {};

    const defaultBtns = [
      showIf(isGiftWrapAvailable || isGiftMessagAvailable)(
        <button style={style} key={`${item.item_id}-gift-options`} onClick={() => openGiftOptions(itemId)}>
          Gift Options
        </button>
      ),
      showIf(removeable)(
        <button
          style={style}
          disabled={cartItemsAreFetching}
          key={`${item.item_id}-remove`}
          onClick={() => removeCartItemRequest(item)}
        >
          Remove
        </button>
      )
    ];

    return [...defaultBtns];
  };

  const cartItemsAreFetching = isFetching(cartItemLoadingState);

  if (cartItems.length > 0) {
    const cartProducts = productSelectors.findProductsByIds(cartItems.map((item) => parseInt(item.product_id)));

    return (
      <div>
        <StyledCartItemList>
          {cartItems.map((item: AUQuoteDataCartItem) => {
            const cartItemProduct = cartProducts.find((product) => product.sku === item.sku);
            return (
              <li key={item.item_id}>
                <CartItem
                  isCartItemFetching={cartItemsAreFetching}
                  item={item}
                  cartItemProduct={cartItemProduct}
                  handleQtyChange={(value: Option) => updateCartItemQuantity(value)(item)}
                  flashId={flashId}
                  flashToken={flashToken}
                  email={email}
                  customerId={customerId}
                  buttons={getButtonList(item, productOptionPriceTransparencySplit)}
                  productOptionPriceTransparencySplit={productOptionPriceTransparencySplit}
                />
              </li>
            );
          })}
        </StyledCartItemList>
      </div>
    );
  } else if ((isEmpty(cartItems) && isSuccess(cartLoadingState)) || isNotFound(cartLoadingState)) {
    return (
      <EmptyCartContent>
        <StyledP secondary>You have no items in your shopping cart.</StyledP>
        <StyledP secondary>
          Click{' '}
          <ULink id="continue-shopping" path="/">
            here
          </ULink>{' '}
          to continue shopping.
        </StyledP>
      </EmptyCartContent>
    );
  }

  return <CartItemListSkeleton />;
};

const mapStateToProps = (state: GlobalState) => ({
  cartItems: cartItemsSelector(state),
  customAttributes: customerCustomAttributesSelector(state),
  cartLoadingState: cartLoadingStateSelector(state),
  cartItemLoadingState: cartItemsLoadingStateSelector(state),
  email: customerEmailSelector(state),
  customerId: customerIdSelector(state)
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateCartItemQuantity: (quantity: Option) => (cartItem: QuoteDataCartItem) =>
    dispatch(updateCartItemQuantityRequest(quantity.value, cartItem)),
  removeCartItemRequest: (item: QuoteDataCartItem) => dispatch(_removeCartItemRequest(item)),
  fetchCartRequest: () => dispatch(_fetchCartRequest()),
  openGiftOptions: (data) => dispatch(openGiftOptionsModal(data))
});

export default connect(mapStateToProps, mapDispatchToProps)(CartItemList);
